import dynamicfunction from '../../../utils/dynamicfunction';
import { fontSizes } from '../../../constants/constants';
import { Button, ConfirmModal, Dropdown, ErrorText, InputField, RadioButton, Text } from '../../../components/SecondaryMarket';
import ViewReverse from '../../../components/ViewReverse';
import icons from '../../../constants/icons';
import { yes_no_options } from '../../../config/config';
import { ReactSVG } from 'react-svg';
import { cn, formatNumbers, getContentKeyAccToLang } from '../../../utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { useState } from 'react';
import {
  bank_and_custodian_information_schema,
  bank_and_custodian_information_validation_schema,
  bank_details_schema,
  bank_details_validation_schema,
} from '../../../validations_schemas/bank_and_custodian_information_validation_schema';
import ViewItems from '../../../components/ViewItems';
import BankEditModal from './BankEditModal';
import { isEmptyValue, alphabet_numeric_regix, alphabet_regix, getRejectedFieldData, isBankValuesExist, numeric_integer_regix } from '../../../utils/functions';
import { validateIbanKyc } from '../../../store/actions/corporate-action';
import { validateAccountOpeningIban } from '../../../store/actions/accountOpeningAction';

const BankAndCustodianInformation = ({ isKYC, isOpen = true, setStep, contentKey, formRef, percentage }) => {
  const { TextWeight, isArabic } = dynamicfunction();
  const dispatch = useDispatch()

  const corpKycContent = useSelector((state) => state.content?.corpKycContent);
  const corpAOContent = useSelector((state) => state.content?.corpAOContent);
  const applicationId = useSelector((state) => state.content?.applicationId);
  const user = useSelector((state) => state.content?.user);
  const { corporate_kyc_data, corporate_dropdown_data, rejection_summary, is_rejected, field_codes, corporate_loading } = useSelector((state) => state.corporate);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({ arrayType: null, index: null });

  const bankAndCustodianData = isKYC ? corporate_kyc_data?.kycCustodianInformation : corporate_kyc_data?.accountOpeningCustodianInformation
  const internationalBankData = isKYC ? corporate_kyc_data?.kycInternationalBank : corporate_kyc_data?.accountOpeningInternationalBank
  const ksaBankData = isKYC ? corporate_kyc_data?.kycKsaBank : corporate_kyc_data?.accountOpeningKsaBank

  const getContentKey = (key) => {
    const content = isKYC ? corpKycContent : corpAOContent;
    return getContentKeyAccToLang(content?.[contentKey]?.data?.attributes, key, isArabic);
  };

  const rejectedFields = rejection_summary?.find(item => item?.sectionId === 6)?.rejectedFields || [];
  const rejectedKSAField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, isKYC ? 'kycKsaBank' : 'accountOpeningKsaBank');
  const rejectedInternationalField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, isKYC ? 'kycInternationalBank' : "accountOpeningInternationalBank");
  const haveCustodianField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'isHaveCustodian');
  const certificatesField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'toWhomShouldWeSendEachOfTheFollowingClient');
  const dividendsField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'toWhomShouldWeSendEachOfTheFollowingDividends');
  const salesProceedsField = rejectedFields?.length < 1 ? null : getRejectedFieldData(rejectedFields, field_codes, 'toWhomShouldWeSendEachOfTheFollowingSalesProceeds');

  const isBankRejected = rejectedKSAField?.remarks?.length > 0 || rejectedInternationalField?.remarks?.length > 0;

  const handleDeleteClick = (arrayType, index) => {
    setModalInfo({ arrayType, index });
    setDeleteModal(true);
  };

  const handleEditClick = (arrayType, index) => {
    setModalInfo({ arrayType, index });
    setEditModal(true);
  };

  const handleCloseEditModal = () => {
    setEditModal(false);
    setModalInfo({ arrayType: null, index: null });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal(false);
    setModalInfo({ arrayType: null, index: null });
  };

  const getEditBankInitialValues = () => {
    const formValues = formRef?.current?.values;
    const isInternational = modalInfo?.arrayType === 'international';
    const bankData = isInternational ? formValues?.internationalBank?.[modalInfo?.index] : formValues?.ksaBank?.[modalInfo?.index];
    const bankTypeId = isInternational ? 1062 : 1061;

    return {
      bankTypeId,
      bankName: bankData?.bankName ?? '',
      bankId: bankData?.bankId ?? null,
      branch: bankData?.branch ?? '',
      beneficiaryName: bankData?.beneficiaryName ?? '',
      iban: bankData?.iban ?? '',
      accountNumber: bankData?.accountNumber ?? '',
      registeredAddress: bankData?.registeredAddress ?? '',
      abaRoutingNumber: bankData?.abaRoutingNumber ?? '',
      swiftCode: bankData?.swiftCode ?? '',
    };
  };

  const handleConfirmDelete = () => {
    const formikInstance = formRef?.current;
    if (modalInfo?.arrayType === 'international') {
      const currentValue = formikInstance?.values?.internationalBank || [];
      formikInstance?.setFieldValue('internationalBank', currentValue.filter((_, i) => i !== modalInfo.index));
    } else if (modalInfo?.arrayType === 'ksa') {
      const currentValue = formikInstance?.values?.ksaBank || [];
      formikInstance?.setFieldValue('ksaBank', currentValue.filter((_, i) => i !== modalInfo.index));
    }
    handleCloseDeleteModal();
  };

  const handleEditBankSubmit = (bankValues, { resetForm }) => {
    const data = {
      corpClientApplicationId: applicationId,
      iban: bankValues?.iban,
    };
    const formValues = formRef?.current?.values;

    const valuesExists = dispatch(isBankValuesExist(formValues?.ksaBank?.filter((_, ind) => ind !== modalInfo.index), formValues?.internationalBank?.filter((_, ind) => ind !== modalInfo.index), bankValues, getContentKey));
    if (valuesExists) return;

    const isInternational = bankValues?.bankTypeId === 1062;
    const updatedBank = isInternational
      ? {
        branch: bankValues?.branch,
        beneficiaryName: bankValues?.beneficiaryName,
        iban: bankValues?.iban?.toUpperCase(),
        bankName: bankValues?.bankName,
        accountNumber: bankValues?.accountNumber,
        registeredAddress: bankValues?.registeredAddress,
        abaRoutingNumber: bankValues?.abaRoutingNumber,
        swiftCode: bankValues?.swiftCode?.toUpperCase(),
      }
      : {
        branch: bankValues?.branch,
        beneficiaryName: bankValues?.beneficiaryName,
        iban: bankValues?.iban?.toUpperCase(),
        bankId: bankValues?.bankId,
        bankName: bankValues?.bankName
      };
    if (isInternational) {
      const nextInternational = [...(formValues?.internationalBank || [])];
      nextInternational.splice(modalInfo?.index, 1, updatedBank);
      formRef?.current?.setFieldValue('internationalBank', [...nextInternational]);
      resetForm({ values: getEditBankInitialValues() });
      handleCloseEditModal();
    } else {
      isKYC ? (
        dispatch(
          validateIbanKyc(data, (res) => {
            if (res?.result) {
              const nextKsa = [...(formValues?.ksaBank || [])];
              nextKsa.splice(modalInfo?.index, 1, updatedBank);
              formRef?.current?.setFieldValue('ksaBank', [...nextKsa]);
              resetForm({ values: getEditBankInitialValues() });
              handleCloseEditModal();
            }

          })
        )
      ) : (
        dispatch(
          validateAccountOpeningIban(data, (res) => {
            if (res?.result) {
              const nextKsa = [...(formValues?.ksaBank || [])];
              nextKsa.splice(modalInfo?.index, 1, updatedBank);
              formRef?.current?.setFieldValue('ksaBank', [...nextKsa]);
              resetForm({ values: getEditBankInitialValues() });
              handleCloseEditModal();
            }

          })
        )
      )
    }
  };

  return (
    <div>
      <ViewReverse className={cn('w-full items-center gap-2 justify-between', isKYC && 'cursor-pointer')} onClick={() => isKYC && setStep(5)}>
        <ViewReverse className="flex items-center gap-2">
          <Text text={getContentKey('bank_custodian_information')} weight={TextWeight.headingRegular} className={'text-dark-blue'} size={fontSizes.h30} />
          {rejectedFields?.length > 0 && <ReactSVG src={icons.heading_info_outline_icon} />}
        </ViewReverse>

        <ViewReverse className={'items-center gap-2'}>
          <Text text={`${formatNumbers(percentage) ?? 0}%`} className={'text-dark-blue'} size={fontSizes.h14} />
          {isKYC && <ReactSVG src={icons.downArrow} className={cn(isOpen ? 'rotate-180' : 'rotate-0')} />}
        </ViewReverse>
      </ViewReverse>

      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={bank_and_custodian_information_schema({ ...bankAndCustodianData, internationalBank: internationalBankData, ksaBank: ksaBankData })}
        validationSchema={bank_and_custodian_information_validation_schema}
        onSubmit={() => { }}
        validateOnChange={false}
        validateOnMount={false}
        validateOnBlur={false}
      >
        {({ values, errors, setFieldValue, setFieldError }) => {
          const is_bank_change = !(JSON.stringify(values?.ksaBank) === JSON.stringify(ksaBankData) && JSON.stringify(values?.internationalBank) === JSON.stringify(internationalBankData));

          return (
            isOpen && (
              <>
                {isKYC && <div className='mt-8 h-[1px] bg-black/10' />}

                <Text
                  text={getContentKey('existing_ksa_bank')} isEmpty={isEmptyValue([...(values?.ksaBank ?? []), ...(values?.internationalBank ?? [])])}
                  className={'text-primary-light mt-10'}
                  size={fontSizes.h14}
                  isRequired
                />

                <div className='max-w-full overflow-x-auto bg-light-background p-6 rounded-lg mt-1'>
                  <ViewReverse className={cn('w-max gap-4', isArabic && 'ml-auto')}>
                    <Text text={getContentKey('bank_name')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-32 max-w-32'} />
                    <Text text={getContentKey('branch')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-24 max-w-24'} />
                    <Text text={getContentKey('beneficiary_name')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-40 max-w-40'} />
                    <Text text={getContentKey('iban')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-60 max-w-60'} />
                    <Text text={getContentKey('account_number')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-40 max-w-40'} />
                    <Text text={getContentKey('registered_address')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-40 max-w-40'} />
                    <Text text={getContentKey('aba_routing')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-40 max-w-40'} />
                    <Text text={getContentKey('swift_code')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-40 max-w-40'} />
                    <Text text={getContentKey('action')} size={fontSizes.h14} weight={TextWeight.bold} className={'text-black min-w-16 max-w-16'} />
                  </ViewReverse>
                  {
                    values?.internationalBank?.map((bank, index) => {
                      const isEditable = (is_rejected && isBankRejected) || !is_rejected;
                      return (
                        <ViewReverse className={cn('w-max mt-2 gap-4', isArabic && 'ml-auto')} key={`intl-${index}`}>
                          <Text text={bank?.bankName || "-"} size={fontSizes.h14} className={'min-w-32 max-w-32 break-all'} />
                          <Text text={bank?.branch || "-"} size={fontSizes.h14} className={'min-w-24 max-w-24 break-all'} />
                          <Text text={bank?.beneficiaryName || "-"} size={fontSizes.h14} className={'min-w-40 max-w-40 break-all'} />
                          <Text text={bank?.iban || "-"} size={fontSizes.h14} className={'min-w-60 max-w-60 break-all'} />
                          <Text text={bank?.accountNumber || "-"} size={fontSizes.h14} className={'min-w-40 max-w-40 break-all'} />
                          <Text text={bank?.registeredAddress || "-"} size={fontSizes.h14} className={'min-w-40 max-w-40 break-all'} />
                          <Text text={bank?.abaRoutingNumber || "-"} size={fontSizes.h14} className={'min-w-40 max-w-40 break-all'} />
                          <Text text={bank?.swiftCode || "-"} size={fontSizes.h14} className={'min-w-40 max-w-40'} />
                          <ViewReverse className={'gap-3 min-w-16 max-w-16'}>
                            <ReactSVG
                              src={icons.edit_icon}
                              className={cn(isEditable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50')}
                              onClick={() => isEditable && handleEditClick('international', index)}
                            />
                            <ReactSVG
                              src={icons.delete_outline_icon}
                              className={cn(isEditable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50')}
                              onClick={() => isEditable && handleDeleteClick('international', index)}
                            />
                          </ViewReverse>
                        </ViewReverse>
                      )
                    })
                  }
                  {
                    values?.ksaBank?.map((bank, index) => {
                      const isEditable = (is_rejected && isBankRejected) || !is_rejected;
                      return (
                        <ViewReverse className={cn('w-max mt-2 gap-4', isArabic && 'ml-auto')} key={`ksa-${index}`}>
                          <Text text={corporate_dropdown_data?.banks?.find((item) => item?.id === bank?.bankId)?.name || "-"} size={fontSizes.h14} className={'min-w-32 max-w-32'} />
                          <Text text={bank?.branch || "-"} size={fontSizes.h14} className={'min-w-24 max-w-24'} />
                          <Text text={bank?.beneficiaryName || "-"} size={fontSizes.h14} className={'min-w-40 max-w-40'} />
                          <Text text={bank?.iban || "-"} size={fontSizes.h14} className={'min-w-60 max-w-60 break-all'} />
                          <Text text={"-"} size={fontSizes.h14} className={'min-w-40 max-w-40'} />
                          <Text text={"-"} size={fontSizes.h14} className={'min-w-40 max-w-40'} />
                          <Text text={"-"} size={fontSizes.h14} className={'min-w-40 max-w-40'} />
                          <Text text={"-"} size={fontSizes.h14} className={'min-w-40 max-w-40'} />
                          <ViewReverse className={'gap-3 min-w-16 max-w-16'}>
                            <ReactSVG
                              src={icons.edit_icon}
                              className={cn(isEditable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50')}
                              onClick={() => isEditable && handleEditClick('ksa', index)}
                            />
                            <ReactSVG
                              src={icons.delete_outline_icon}
                              className={cn(isEditable ? 'cursor-pointer' : 'cursor-not-allowed opacity-50')}
                              onClick={() => isEditable && handleDeleteClick('ksa', index)}
                            />
                          </ViewReverse>
                        </ViewReverse>
                      )
                    })
                  }
                </div>
                {
                  !is_bank_change && isBankRejected && (
                    <ViewReverse className='items-center gap-2 mt-2'>
                      <ReactSVG src={icons.field_info_outline} />
                      <ErrorText text={rejectedKSAField?.remarks || rejectedInternationalField?.remarks} className={'mt-0'} />
                    </ViewReverse>
                  )
                }

                {
                  ((values?.ksaBank?.length ?? 0) + (values?.internationalBank?.length ?? 0)) !== 5 && (
                    <>
                      <Text
                        text={getContentKey('bank_account_to_add')}
                        className={'mt-6 text-primary-light'}
                        size={fontSizes.h14}
                        isRequired
                      />

                      <ViewReverse className={'gap-6 mt-3'}>
                        {
                          corporate_dropdown_data?.bankTypes?.map((item, index) => (
                            <RadioButton
                              label={item?.name}
                              selected={values?.bankTypeId === item?.id}
                              onClick={() => {
                                if (values?.bankTypeId === item?.id) {
                                  setFieldValue('bankTypeId', null);
                                } else {
                                  setFieldValue('bankTypeId', item?.id)
                                }
                                setFieldError('bankTypeId', null);
                              }}
                              disabled={is_rejected && (!rejectedKSAField?.remarks && !rejectedInternationalField?.remarks)}
                            />
                          ))
                        }
                      </ViewReverse>
                      <ErrorText text={getContentKey(errors?.bankTypeId)} />
                      <div className='my-10 h-[1px] bg-black/10'></div>
                      <Formik
                        enableReinitialize
                        initialValues={bank_details_schema(values?.bankTypeId, isKYC ? user : null)}
                        validationSchema={bank_details_validation_schema}
                        validateOnChange={false}
                        validateOnMount={false}
                        validateOnBlur={false}
                        onSubmit={(bankValues, { resetForm: resetBankForm }) => {
                          const data = {
                            corpClientApplicationId: applicationId,
                            iban: bankValues?.iban,
                          };

                          const valuesExists = dispatch(isBankValuesExist(values?.ksaBank, values?.internationalBank, bankValues, getContentKey));
                          if (valuesExists) return;

                          if (values?.bankTypeId === 1062) {
                            setFieldValue('internationalBank', [
                              ...values?.internationalBank,
                              {
                                branch: bankValues?.branch,
                                beneficiaryName: bankValues?.beneficiaryName,
                                iban: bankValues?.iban?.toUpperCase(),
                                bankName: bankValues?.bankName,
                                accountNumber: bankValues?.accountNumber,
                                registeredAddress: bankValues?.registeredAddress,
                                abaRoutingNumber: bankValues?.abaRoutingNumber,
                                swiftCode: bankValues?.swiftCode?.toUpperCase(),
                              },
                            ]);
                          } else {
                            isKYC ? (
                              dispatch(
                                validateIbanKyc(data, (res) => {
                                  if (res?.result) {
                                    setFieldValue('ksaBank', [
                                      ...values?.ksaBank,
                                      {
                                        branch: bankValues?.branch,
                                        beneficiaryName: bankValues?.beneficiaryName,
                                        iban: bankValues?.iban?.toUpperCase(),
                                        bankId: bankValues?.bankId,
                                        bankName: bankValues?.bankName
                                      },
                                    ]);

                                  }
                                })
                              )
                            ) : (
                              dispatch(
                                validateAccountOpeningIban(data, (res) => {
                                  if (res?.result) {
                                    setFieldValue('ksaBank', [
                                      ...values?.ksaBank,
                                      {
                                        branch: bankValues?.branch,
                                        beneficiaryName: bankValues?.beneficiaryName,
                                        iban: bankValues?.iban?.toUpperCase(),
                                        bankId: bankValues?.bankId,
                                        bankName: bankValues?.bankName
                                      },
                                    ]);

                                  }
                                })
                              )
                            )
                          }
                          resetBankForm({ values: bank_details_schema(values?.bankTypeId, isKYC ? user : null) });
                        }}
                      >
                        {({ values: bankValues, errors: bankErrors, setFieldValue: setBankFieldValue, handleSubmit, resetForm: resetBankForm }) => {
                          const isDisabled = !values?.bankTypeId;
                          const is_ksa = values?.bankTypeId === 1061;

                          return (
                            <div className='bg-light-background p-6 rounded-lg skip-required-highlight'>
                              <Text text={getContentKey('bank_details')} className={'text-light-blue'} size={fontSizes.h22} />

                              <div className='space-y-6'>
                                <ViewReverse className={'gap-6 mt-8'} isResponsive>
                                  {
                                    values?.bankTypeId === 1061 ? (
                                      <Dropdown
                                        label={getContentKey('bank')}
                                        valueKey={'name'}
                                        value={bankValues?.bankId ? corporate_dropdown_data?.banks?.find((v) => v?.id === bankValues?.bankId) : null}
                                        isRequired={false}
                                        options={corporate_dropdown_data?.banks}
                                        onSelect={(val) => setBankFieldValue('bankId', val?.id)}
                                        error={getContentKey(bankErrors?.bankId)}
                                        isDisabled={isDisabled}
                                      />
                                    ) : (
                                      <InputField
                                        label={getContentKey('bank_name')}
                                        isRequired
                                        placeholder={getContentKey('enter_bank_name')}
                                        className={'bg-white'}
                                        value={bankValues?.bankName}
                                        isArabicAllowed
                                        maxLength={50}
                                        onChange={(text) => {
                                          setBankFieldValue('bankName', text)
                                        }}
                                        error={getContentKey(bankErrors?.bankName)}
                                        isDisabled={isDisabled}
                                      />
                                    )
                                  }
                                  <InputField
                                    label={getContentKey('branch')}
                                    isRequired
                                    placeholder={getContentKey('enter_branch')}
                                    className={'bg-white'}
                                    value={bankValues?.branch}
                                    maxLength={50}
                                    isArabicAllowed
                                    onChange={(text) => {
                                      if (alphabet_regix.test(text)) {
                                        setBankFieldValue('branch', text)
                                      }
                                    }
                                    }
                                    error={getContentKey(bankErrors?.branch)}
                                    isDisabled={isDisabled}
                                  />
                                  <InputField
                                    label={getContentKey('beneficiary_name')}
                                    isRequired
                                    placeholder={getContentKey('enter_beneficiary_name')}
                                    className={'bg-white'}
                                    isArabicAllowed
                                    maxLength={50}
                                    value={bankValues?.beneficiaryName}
                                    onChange={(text) => {
                                      if (alphabet_regix.test(text)) {
                                        setBankFieldValue('beneficiaryName', text)
                                      }
                                    }
                                    }
                                    error={getContentKey(bankErrors?.beneficiaryName)}
                                    isDisabled={isKYC ? true : isDisabled}
                                  // isDisabled={isDisabled}
                                  />
                                </ViewReverse>

                                <ViewReverse className={'gap-6 mt-8'} isResponsive>
                                  <InputField
                                    label={getContentKey('iban')}
                                    isRequired
                                    inputClasses={bankValues?.iban && 'uppercase'}
                                    placeholder={getContentKey('enter_iban')}
                                    className={'bg-white'}
                                    value={bankValues?.iban}
                                    onChange={(text) => {
                                      let value = text.toUpperCase();
                                      if (!text.startsWith('SA') && is_ksa) {
                                        value = 'SA';
                                      }
                                      if (alphabet_numeric_regix.test(text)) {
                                        setBankFieldValue('iban', value)
                                      }
                                    }
                                    }
                                    error={getContentKey(bankErrors?.iban)}
                                    maxLength={!is_ksa ? 34 : 24}
                                    isDisabled={isDisabled}
                                  />

                                  {
                                    !is_ksa ? (
                                      <>
                                        <InputField
                                          label={getContentKey('account_number')}
                                          isRequired
                                          placeholder={getContentKey('enter_account_number')}
                                          className={'bg-white'}
                                          maxLength={34}
                                          value={bankValues?.accountNumber}
                                          onChange={(text) => {
                                            setBankFieldValue('accountNumber', text)
                                          }
                                          }
                                          error={getContentKey(bankErrors?.accountNumber)}
                                          isDisabled={isDisabled}
                                        />
                                        <InputField
                                          label={getContentKey('registered_address')}
                                          isRequired
                                          isArabicAllowed
                                          placeholder={getContentKey('enter_registered_address')}
                                          className={'bg-white'}
                                          maxLength={70}
                                          value={bankValues?.registeredAddress}
                                          onChange={(text) => setBankFieldValue('registeredAddress', text)}
                                          error={getContentKey(bankErrors?.registeredAddress)}
                                          isDisabled={isDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <div className='w-full hidden md:flex' />
                                        <div className='w-full hidden md:flex' />
                                      </>
                                    )
                                  }
                                </ViewReverse>

                                {
                                  values?.bankTypeId === 1062 && (
                                    <ViewReverse className={'gap-6 mt-8'} isResponsive>
                                      <InputField
                                        label={getContentKey('aba_routing_number')}
                                        isRequired
                                        placeholder={getContentKey('enter_aba_routing_number')}
                                        className={'bg-white'}
                                        value={bankValues?.abaRoutingNumber}
                                        maxLength={9}
                                        onChange={(text) => {
                                          if (numeric_integer_regix.test(text)) {
                                            setBankFieldValue('abaRoutingNumber', text)
                                          }
                                        }
                                        }
                                        error={getContentKey(bankErrors?.abaRoutingNumber)}
                                      />
                                      <InputField
                                        label={getContentKey('swift_code')}
                                        isRequired
                                        placeholder={getContentKey('enter_swift_code')}
                                        className={'bg-white'}
                                        maxLength={11}
                                        inputClasses={bankValues?.swiftCode && 'uppercase'}
                                        value={bankValues?.swiftCode}
                                        onChange={(text) => {
                                          if (alphabet_numeric_regix.test(text)) {
                                            setBankFieldValue('swiftCode', text)
                                          }
                                        }}
                                        error={getContentKey(bankErrors?.swiftCode)}
                                      />
                                      <div className='w-full hidden md:flex' />
                                    </ViewReverse>
                                  )
                                }
                              </div>

                              <ViewReverse className={'gap-2 mt-10'}>
                                <ReactSVG src={icons.info_red_icon} />
                                <Text text={getContentKey('atleast_one_bank_required')} className={'text-red-600'} />
                              </ViewReverse>
                              <ViewReverse className={'gap-2 mt-4'}>
                                <ReactSVG src={icons.info_red_icon} />
                                <Text text={getContentKey('max_five_accounts')} className={'text-red-600'} />
                              </ViewReverse>
                              <ViewReverse className={'gap-2 mt-4'}>
                                <ReactSVG src={icons.info_red_icon} />
                                <Text text={getContentKey('beneficiary_name_validation')} className={'text-red-600'} />
                              </ViewReverse>

                              <ViewReverse className={'w-full mt-5 justify-end gap-2'} isResponsive>
                                <Button
                                  text={getContentKey('clear')}
                                  className={'w-full md:w-44 bg-white border border-grey text-black'}
                                  weight={TextWeight.bold}
                                  textSize={fontSizes.h14}
                                  isDisabled={isDisabled}
                                  onClick={() => resetBankForm({ values: bank_details_schema(values?.bankTypeId, isKYC ? user : null) })}
                                />
                                <Button
                                  text={getContentKey('add')}
                                  className={'w-full md:w-44 bg-light-blue border-none'}
                                  weight={TextWeight.bold}
                                  textSize={fontSizes.h14}
                                  isDisabled={isDisabled || corporate_loading}
                                  loading={corporate_loading}
                                  onClick={handleSubmit}
                                />
                              </ViewReverse>
                            </div>
                          )
                        }}
                      </Formik>
                    </>
                  )
                }


                {/* {!isKYC && (
                  <Button
                    text={'Add signatory'}
                    weight={TextWeight.bold}
                    textSize={fontSizes.h14}
                    className={'bg-white !mt-9 border border-grey text-dark-blue w-full'}
                    icon={icons.plus_icon}
                    onClick={() => { }}
                  />
                )} */}

                <div className='my-10 h-[1px] bg-black/10'></div>

                <Text text={getContentKey('custodian_details')} className={'text-light-blue'} size={fontSizes.h22} />

                <div className='space-y-6'>

                  <Text
                    text={getContentKey('do_you_have_custodian')} isEmpty={isEmptyValue(values?.isHaveCustodian)}
                    className={'text-primary-light mt-8'}
                    size={fontSizes.h14}
                    isRequired
                  />
                  <ViewReverse className={'gap-6 mt-3'}>
                    {
                      yes_no_options?.map((item) => (
                        <RadioButton
                          label={getContentKey(item?.name)}
                          selected={values?.isHaveCustodian === item?.value}
                          onClick={() => {
                            if (values?.isHaveCustodian === item?.value) {
                              setFieldValue('isHaveCustodian', null);
                            } else {
                              setFieldValue('isHaveCustodian', item?.value)
                            }
                            setFieldError('isHaveCustodian', null);
                          }}
                          remarksMetadata={values?.isHaveCustodian !== bankAndCustodianData?.isHaveCustodian ? null : {
                            sectionId: 6,
                            key: 'isHaveCustodian',
                          }}
                        />
                      ))
                    }
                  </ViewReverse>
                  {
                    haveCustodianField?.remarks?.length > 0 && values?.isHaveCustodian === bankAndCustodianData?.isHaveCustodian && (
                      <ViewReverse className='items-center gap-2 mt-2'>
                        <ReactSVG src={icons.field_info_outline} />
                        <ErrorText text={haveCustodianField?.remarks} className={'mt-0'} />
                      </ViewReverse>
                    )
                  }
                  <ErrorText text={getContentKey(errors?.isHaveCustodian)} />

                  {
                    values?.isHaveCustodian && (
                      <>
                        <ViewReverse className={'gap-6 mt-8'} isResponsive>
                          <InputField
                            label={getContentKey('custodian_name')}
                            isRequired
                            isArabicAllowed
                            maxLength={50}
                            placeholder={getContentKey('enter_custodian_name')}
                            value={values?.custodianName}
                            onChange={(text) => {
                              if (alphabet_regix.test(text)) {
                                setFieldValue('custodianName', text)
                              }
                            }
                            }
                            error={getContentKey(errors?.custodianName)}
                            remarksMetadata={
                              !(haveCustodianField?.remarks?.length > 0) &&
                              (values?.custodianName === bankAndCustodianData?.custodianName) && {
                                sectionId: 6,
                                key: 'custodianName',
                              }}
                          />
                          <InputField
                            label={getContentKey('address')}
                            isRequired
                            isArabicAllowed
                            placeholder={getContentKey('enter_address')}
                            value={values?.address}
                            onChange={(text) => setFieldValue('address', text)}
                            maxLength={100}
                            error={getContentKey(errors?.address)}
                            remarksMetadata={
                              !(haveCustodianField?.remarks?.length > 0) &&
                              (values?.address === bankAndCustodianData?.address) && {
                                sectionId: 6,
                                key: 'address',
                              }}
                          />
                          <InputField
                            label={getContentKey('account_name')}
                            isRequired
                            isArabicAllowed
                            placeholder={getContentKey('enter_account_name')}
                            value={values?.accountName}
                            maxLength={50}
                            onChange={(text) => {
                              if (alphabet_regix.test(text)) {
                                setFieldValue('accountName', text)
                              }
                            }
                            }
                            error={getContentKey(errors?.accountName)}
                            remarksMetadata={
                              !(haveCustodianField?.remarks?.length > 0) &&
                              (values?.accountName === bankAndCustodianData?.accountName) && {
                                sectionId: 6,
                                key: 'accountName',
                              }}
                          />
                        </ViewReverse>

                        <ViewReverse className={'gap-6 mt-8'} isResponsive>
                          <InputField
                            label={getContentKey('account_number')}
                            isRequired
                            placeholder={getContentKey('enter_account_number')}
                            value={values?.accountNumber}
                            maxLength={50}
                            onChange={(text) => {
                              if (numeric_integer_regix.test(text)) {
                                setFieldValue('accountNumber', text)
                              }
                            }
                            }
                            error={getContentKey(errors?.accountNumber)}
                            remarksMetadata={
                              !(haveCustodianField?.remarks?.length > 0) &&
                              (values?.accountNumber === bankAndCustodianData?.accountNumber) && {
                                sectionId: 6,
                                key: 'accountNumber',
                              }}
                          />
                          <div className='w-full hidden md:flex' />
                          <div className='w-full hidden md:flex' />
                        </ViewReverse>
                      </>
                    )
                  }
                </div>

                <div className='space-y-6 mt-10'>
                  <Text text={getContentKey('whom_we_send')} className={'text-primary-light'} size={fontSizes.h14} isRequired />

                  <ViewReverse className={'justify-between md:items-center gap-2'} isResponsive>
                    <Text text={getContentKey('certificates')} isEmpty={isEmptyValue(values?.toWhomShouldWeSendEachOfTheFollowingClient)} className={'text-primary-light'} size={fontSizes.h14} />

                    <ViewItems>
                      <ViewReverse className={'gap-2 md:gap-6 md:items-center justify-end'} isResponsive>
                        {corporate_dropdown_data?.custodianDetails?.map((item) => (
                          <RadioButton
                            key={item?.id}
                            label={item?.name}
                            labelClasses={'whitespace-nowrap'}
                            selected={values?.toWhomShouldWeSendEachOfTheFollowingClient === item?.id}
                            className={'py-2.5'}
                            onClick={() => {
                              if (values?.toWhomShouldWeSendEachOfTheFollowingClient === item?.id) {
                                setFieldValue('toWhomShouldWeSendEachOfTheFollowingClient', null);
                              } else {
                                setFieldValue('toWhomShouldWeSendEachOfTheFollowingClient', item?.id)
                              }
                              setFieldError('toWhomShouldWeSendEachOfTheFollowingClient', null);
                            }}
                            remarksMetadata={values?.toWhomShouldWeSendEachOfTheFollowingClient !== bankAndCustodianData?.toWhomShouldWeSendEachOfTheFollowingClient ? null : {
                              sectionId: 6,
                              key: 'toWhomShouldWeSendEachOfTheFollowingClient',
                            }}
                          />
                        ))}
                        {values?.toWhomShouldWeSendEachOfTheFollowingClient === 803 && (
                          <InputField
                            placeholder={getContentKey('enter_certificates')}
                            isArabicAllowed
                            value={values?.toWhomShouldWeSendEachOfTheFollowingClientOther}
                            onChange={(text) => setFieldValue('toWhomShouldWeSendEachOfTheFollowingClientOther', text)}
                            className={'w-full md:w-72'}
                            maxLength={50}
                            error={getContentKey(errors?.toWhomShouldWeSendEachOfTheFollowingClientOther)}
                            remarksMetadata={values?.toWhomShouldWeSendEachOfTheFollowingClient !== bankAndCustodianData?.toWhomShouldWeSendEachOfTheFollowingClient ? null : {
                              sectionId: 6,
                              key: 'toWhomShouldWeSendEachOfTheFollowingClient',
                            }}
                          />
                        )}
                      </ViewReverse>
                      {
                        certificatesField?.remarks?.length > 0 && values?.toWhomShouldWeSendEachOfTheFollowingClient === bankAndCustodianData?.toWhomShouldWeSendEachOfTheFollowingClient && (
                          <ViewReverse className='items-center gap-2 mt-2'>
                            <ReactSVG src={icons.field_info_outline} />
                            <ErrorText text={certificatesField?.remarks} className={'mt-0'} />
                          </ViewReverse>
                        )
                      }
                    </ViewItems>
                  </ViewReverse>
                  <ErrorText text={getContentKey(errors?.toWhomShouldWeSendEachOfTheFollowingClient)} />

                  <ViewReverse className={'justify-between md:items-center gap-2'} isResponsive>
                    <Text text={getContentKey('dividends_other_income')} isEmpty={isEmptyValue(values?.toWhomShouldWeSendEachOfTheFollowingDividends)} className={'text-primary-light'} size={fontSizes.h14} />

                    <ViewItems>
                      <ViewReverse className={'gap-2 md:gap-6 md:items-center'} isResponsive>
                        {corporate_dropdown_data?.custodianDetails?.map((item) => (
                          <RadioButton
                            key={item?.id}
                            label={item?.name}
                            selected={values?.toWhomShouldWeSendEachOfTheFollowingDividends === item?.id}
                            className={'py-2.5'}
                            labelClasses={'whitespace-nowrap'}
                            onClick={() => {
                              if (values?.toWhomShouldWeSendEachOfTheFollowingDividends === item?.id) {
                                setFieldValue('toWhomShouldWeSendEachOfTheFollowingDividends', null);
                              } else {
                                setFieldValue('toWhomShouldWeSendEachOfTheFollowingDividends', item?.id)
                              }
                              setFieldError('toWhomShouldWeSendEachOfTheFollowingDividends', null);
                            }}
                            remarksMetadata={values?.toWhomShouldWeSendEachOfTheFollowingDividends !== bankAndCustodianData?.toWhomShouldWeSendEachOfTheFollowingDividends ? null : {
                              sectionId: 6,
                              key: 'toWhomShouldWeSendEachOfTheFollowingDividends',
                            }}
                          />
                        ))}
                        {values?.toWhomShouldWeSendEachOfTheFollowingDividends === 803 && (
                          <InputField
                            placeholder={getContentKey('enter_dividends_other_income')}
                            isArabicAllowed
                            value={values?.toWhomShouldWeSendEachOfTheFollowingDividendsOther}
                            onChange={(text) => setFieldValue('toWhomShouldWeSendEachOfTheFollowingDividendsOther', text)}
                            className={'w-full md:w-72'}
                            error={getContentKey(errors?.toWhomShouldWeSendEachOfTheFollowingDividendsOther)}
                            maxLength={50}
                            remarksMetadata={values?.toWhomShouldWeSendEachOfTheFollowingDividends !== bankAndCustodianData?.toWhomShouldWeSendEachOfTheFollowingDividends ? null : {
                              sectionId: 6,
                              key: 'toWhomShouldWeSendEachOfTheFollowingDividends',
                            }}
                          />
                        )}
                      </ViewReverse>
                      {
                        dividendsField?.remarks?.length > 0 && values?.toWhomShouldWeSendEachOfTheFollowingDividends === bankAndCustodianData?.toWhomShouldWeSendEachOfTheFollowingDividends && (
                          <ViewReverse className='items-center gap-2 mt-2'>
                            <ReactSVG src={icons.field_info_outline} />
                            <ErrorText text={dividendsField?.remarks} className={'mt-0'} />
                          </ViewReverse>
                        )
                      }
                    </ViewItems>
                  </ViewReverse>
                  <ErrorText text={getContentKey(errors?.toWhomShouldWeSendEachOfTheFollowingDividends)} />

                  <ViewReverse className={'justify-between md:items-center gap-2'} isResponsive>
                    <Text text={getContentKey('sales_proceeds')} isEmpty={isEmptyValue(values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds)} className={'text-primary-light'} size={fontSizes.h14} />

                    <ViewItems>
                      <ViewReverse className={'gap-2 md:gap-6 md:items-center'} isResponsive>
                        {corporate_dropdown_data?.custodianDetails?.map((item) => (
                          <RadioButton
                            key={item?.id}
                            label={item?.name}
                            selected={values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds === item?.id}
                            className={'py-2.5'}
                            labelClasses={'whitespace-nowrap'}
                            onClick={() => {
                              if (values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds === item?.id) {
                                setFieldValue('toWhomShouldWeSendEachOfTheFollowingSalesProceeds', null);
                              } else {
                                setFieldValue('toWhomShouldWeSendEachOfTheFollowingSalesProceeds', item?.id)
                              }
                              setFieldError('toWhomShouldWeSendEachOfTheFollowingSalesProceeds', null);
                            }}
                            remarksMetadata={values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds !== bankAndCustodianData?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds ? null : {
                              sectionId: 6,
                              key: 'toWhomShouldWeSendEachOfTheFollowingSalesProceeds',
                            }}
                          />
                        ))}
                        {values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds === 803 && (
                          <InputField
                            placeholder={getContentKey('enter_sales_proceeds')}
                            isArabicAllowed
                            value={values?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther}
                            onChange={(text) => {
                              setFieldValue('toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther', text)
                            }}
                            className={'w-full md:w-72'}
                            error={getContentKey(errors?.toWhomShouldWeSendEachOfTheFollowingSalesProceedsOther)}
                            maxLength={50}
                            remarksMetadata={values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds !== bankAndCustodianData?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds ? null : {
                              sectionId: 6,
                              key: 'toWhomShouldWeSendEachOfTheFollowingSalesProceeds',
                            }}
                          />
                        )}
                      </ViewReverse>
                      {
                        salesProceedsField?.remarks?.length > 0 && values?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds === bankAndCustodianData?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds && (
                          <ViewReverse className='items-center gap-2 mt-2'>
                            <ReactSVG src={icons.field_info_outline} />
                            <ErrorText text={salesProceedsField?.remarks} className={'mt-0'} />
                          </ViewReverse>
                        )
                      }
                    </ViewItems>
                  </ViewReverse>
                  <ErrorText text={getContentKey(errors?.toWhomShouldWeSendEachOfTheFollowingSalesProceeds)} />
                </div>
              </>
            )
          )
        }}
      </Formik>

      <ConfirmModal
        isOpen={deleteModal}
        onClose={handleCloseDeleteModal}
        title={getContentKey('delete_bank')}
        descOne={getContentKey('are_you_sure')}
        leftBtn={getContentKey('cancel')}
        rightBtn={getContentKey('delete')}
        rightBtnClasses={'!bg-red-600 !text-white'}
        onLeftPress={handleCloseDeleteModal}
        onRightPress={handleConfirmDelete}
      />

      <BankEditModal
        isOpen={editModal}
        onClose={handleCloseEditModal}
        getContentKey={getContentKey}
        getEditBankInitialValues={getEditBankInitialValues}
        handleEditBankSubmit={handleEditBankSubmit}
        corporate_dropdown_data={corporate_dropdown_data}
        corporate_loading={corporate_loading}
        isKYC={isKYC}
      />
    </div>
  );
};

export default BankAndCustodianInformation;
